import { request } from "umi";
import { paramsSerializer } from "../api";
import { onSocketReconnected, registerWsHandler, WsEventType } from "../websocket";

const urlPrefix = "api/tenant/automations";

// ---- Graph ------------------------------------------------------------------

export interface AutomationGraphNode {
    id: string;
    type: string;
    params: Record<string, any>;
    position: { x: number; y: number };
}

export interface AutomationGraphEdge {
    id: string;
    source: string;
    target: string;
    /** null for action/trigger edges; 'true' | 'false' for condition branches */
    branch?: "true" | "false" | null;
}

export interface AutomationGraph {
    nodes: AutomationGraphNode[];
    edges: AutomationGraphEdge[];
}

// ---- Automation + runs ------------------------------------------------------

export interface AutomationLastRun {
    id: number;
    dispatch_id: number;
    status: string;
    version?: number | null;
    error?: string | null;
    created_at?: string;
}

export interface Automation {
    id: number;
    tenant_id: number;
    name: string;
    description?: string | null;
    trigger_event: string;
    /** Draft graph — what the builder edits; the engine runs the active version */
    graph: AutomationGraph;
    active_version_id?: number | null;
    is_draft_dirty?: number;
    is_active: number;
    created_at?: string;
    updated_at?: string;
    active_version_number?: number | null;
    version_count?: number;
    run_count?: number;
    last_run?: AutomationLastRun | null;
}

export interface AutomationVersionSummary {
    id: number;
    version: number;
    trigger_event: string;
    published_by_name?: string | null;
    created_at?: string;
    is_live: boolean;
}

export interface AutomationVersionGraph {
    id: number;
    version: number;
    graph: AutomationGraph;
}

export interface AutomationRunStep {
    node_id: string;
    type: string;
    label: string;
    status: "ok" | "failed" | "needs_manual";
    /** Node config + job state before the node ran (absent on legacy runs) */
    input?: { params?: Record<string, any>; job?: Record<string, any> } | null;
    details: Record<string, any>;
    /** Job state after the node ran (absent on legacy runs) */
    output?: { job?: Record<string, any> } | null;
    error?: string | null;
    ms: number;
}

export interface AutomationRun {
    id: number;
    tenant_id: number;
    automation_id: number;
    dispatch_id: number;
    trigger_event: string;
    automation_version_id?: number | null;
    version?: number | null;
    status: "running" | "success" | "skipped" | "failed" | "needs_manual";
    steps?: AutomationRunStep[] | null;
    error?: string | null;
    created_at?: string;
    updated_at?: string;
}

export type AutomationPayload = {
    name: string;
    description?: string | null;
    graph: AutomationGraph;
    is_active?: number;
};

// ---- Builder schema ---------------------------------------------------------

export interface SelectOption {
    value: string | number;
    label: string;
    [key: string]: any;
}

export interface NodeParamSchema {
    key: string;
    label: string;
    /** select | number | technician | technician_list | weekday_list | zones | conditions | status_value | priority_term | service_type_list */
    type: string;
    required?: boolean;
    default?: any;
    min?: number;
    max?: number;
    options?: SelectOption[];
    help?: string;
}

export interface AutomationNodeSchema {
    type: string;
    kind: "trigger" | "condition" | "action";
    label: string;
    description: string;
    params: NodeParamSchema[];
}

export interface ConditionFieldSchema {
    key: string;
    label: string;
    /** enum | boolean | text | zones | source_dispatch_type */
    type: string;
    operators: string[];
    options?: SelectOption[];
    /** Legacy field kept for stored rules — not offered in the builder */
    hidden?: boolean;
}

export interface AutomationBuilderSchema {
    nodes: AutomationNodeSchema[];
    conditions: {
        logic: string[];
        fields: ConditionFieldSchema[];
        operator_labels: Record<string, string>;
    };
    events: SelectOption[];
    options: {
        technicians: SelectOption[];
        zones: SelectOption[];
        /** Tenant's actual (job_source, dispatchType) pairs, value = "source||type" */
        dispatch_types: SelectOption[];
        /** Tenant's actual (job_source, trade) pairs, value = "source||trade" */
        trades: SelectOption[];
        /** Tenant's active priority taxonomy terms, value = term id */
        priorities: SelectOption[];
        /** Tenant's active service_type taxonomy terms, value = term id */
        service_types: SelectOption[];
    };
}

// ---- API --------------------------------------------------------------------

/** [GET] /api/tenant/automations */
export async function getAutomations() {
    return request<API.ResultObject<Automation[]>>(urlPrefix, {
        method: "GET",
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

/** [GET] /api/tenant/automations/schema */
export async function getAutomationSchema() {
    return request<API.ResultObject<AutomationBuilderSchema>>(`${urlPrefix}/schema`, {
        method: "GET",
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

/** [GET] /api/tenant/automations/{id} */
export async function getAutomation(id: number) {
    return request<API.ResultObject<Automation>>(`${urlPrefix}/${id}`, {
        method: "GET",
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

/** [POST] /api/tenant/automations */
export async function addAutomation(data: AutomationPayload) {
    return request<API.ResultObject<Automation>>(urlPrefix, {
        method: "POST",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/automations/{id} */
export async function updateAutomation(id: number, data: Partial<AutomationPayload>) {
    return request<API.ResultObject<Automation>>(`${urlPrefix}/${id}`, {
        method: "PUT",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/automations/{id}/toggle-active */
export async function toggleAutomationActive(id: number) {
    return request<API.ResultObject<Automation>>(`${urlPrefix}/${id}/toggle-active`, {
        method: "PUT",
        withToken: true,
    }).then((res) => res.data);
}

/** [DELETE] /api/tenant/automations/{id} */
export async function deleteAutomation(id: number) {
    return request<API.ResultObject<{ deleted: boolean }>>(`${urlPrefix}/${id}`, {
        method: "DELETE",
        withToken: true,
    }).then((res) => res.data);
}

/** [GET] /api/tenant/automations/{id}/runs */
export async function getAutomationRuns(id: number, page = 1, perPage = 20) {
    return request<
        API.ResultObject<{
            items: AutomationRun[];
            versions: Record<string, AutomationVersionGraph>;
            total: number;
            page: number;
            perPage: number;
        }>
    >(`${urlPrefix}/${id}/runs`, {
        method: "GET",
        params: { page, perPage },
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

/** [POST] /api/tenant/automations/{id}/publish */
export async function publishAutomation(id: number) {
    return request<API.ResultObject<{ automation: Automation; version: number }>>(
        `${urlPrefix}/${id}/publish`,
        {
            method: "POST",
            withToken: true,
        },
    ).then((res) => res.data);
}

/** [GET] /api/tenant/automations/{id}/versions */
export async function getAutomationVersions(id: number) {
    return request<API.ResultObject<AutomationVersionSummary[]>>(`${urlPrefix}/${id}/versions`, {
        method: "GET",
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/automations/{id}/versions/{versionId}/activate */
export async function activateAutomationVersion(id: number, versionId: number) {
    return request<API.ResultObject<{ automation: Automation; version: number }>>(
        `${urlPrefix}/${id}/versions/${versionId}/activate`,
        {
            method: "PUT",
            withToken: true,
        },
    ).then((res) => res.data);
}

/** [PUT] /api/tenant/automations/{id}/versions/{versionId}/restore */
export async function restoreAutomationVersion(id: number, versionId: number) {
    return request<API.ResultObject<Automation>>(`${urlPrefix}/${id}/versions/${versionId}/restore`, {
        method: "PUT",
        withToken: true,
    }).then((res) => res.data);
}

// ---- Auto-schedule failures (topbar badge) ----------------------------------

export interface AutoScheduleFailure {
    dispatch_id: number;
    customer_id?: number | null;
    customer_name?: string | null;
    /** Y-m-d date the CSR requested at creation */
    requested_date?: string | null;
    /** Why no technician could take the date */
    reason?: string | null;
    created_at?: string;
}

/**
 * [GET] /api/tenant/automations/auto-schedule-failures
 * Jobs the automation could not book on their requested date that are still
 * unscheduled — self-clearing once someone schedules them.
 */
export async function getAutoScheduleFailures() {
    return request<API.ResultObject<{ items: AutoScheduleFailure[]; count: number }>>(
        `${urlPrefix}/auto-schedule-failures`,
        {
            method: "GET",
            withToken: true,
        },
    ).then((res) => res.data ?? { items: [], count: 0 });
}

// Signal-only: the backend pushes this when an AUTO_SCHEDULE_FAILED flag is set
// or cleared or a flagged job's appointment changes (TopbarBadgeService), and
// the badge re-fetches over REST. Not coalesced here: the badge spreads and
// coalesces its re-fetch itself (useJitteredReload).
const AUTO_SCHEDULE_FAILURES_UPDATED_EVENT = 'autoScheduleFailuresUpdated';

export function emitAutoScheduleFailuresUpdated() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(AUTO_SCHEDULE_FAILURES_UPDATED_EVENT));
}

/** Subscribe to auto-schedule-failure pushes; returns an unsubscribe function. */
export function onAutoScheduleFailuresUpdated(handler: () => void) {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(AUTO_SCHEDULE_FAILURES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(AUTO_SCHEDULE_FAILURES_UPDATED_EVENT, handler);
}

registerWsHandler(WsEventType.AUTO_SCHEDULE_FAILURES_UPDATED, emitAutoScheduleFailuresUpdated);

// A dropped push during a disconnect never arrives on its own — resync once
// the socket comes back.
onSocketReconnected(emitAutoScheduleFailuresUpdated);
